import Editor from '@monaco-editor/react';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import IndexChart from '../components/IndexChart';
import StrategyStateBadge from '../components/StrategyStateBadge';
import ThemeToggle from '../components/ThemeToggle';
import { useAuth } from '../contexts/AuthContext';
import {
  AdminStrategyRow,
  AdminUser,
  AuditLogEntry,
  EodStatusResponse,
  IndexOverviewRow,
  IndexValuesResponse,
  LoaderResult,
  PENDING_NAME,
  PlatformSettingHistory,
  StrategyState,
  StrategyVersionRow,
  adminApi,
  eodApi,
  indexApi,
} from '../services/api';

// ── Helpers ──────────────────────────────────────────────────────────────────

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
}

function fmtDateTime(iso: string): string {
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

const DOWNLOADABLE: StrategyState[] = ['OUTPUT_READY', 'LIVE', 'UPLOAD_FAILED', 'INACTIVE'];

// ── Sub-components ────────────────────────────────────────────────────────────

function ActionBtn({
  onClick,
  disabled,
  loading,
  variant = 'default',
  title,
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
  variant?: 'default' | 'green' | 'orange' | 'red' | 'ghost';
  title?: string;
  children: React.ReactNode;
}) {
  const base =
    'px-2.5 py-1 text-xs rounded font-medium transition whitespace-nowrap disabled:cursor-not-allowed';
  const styles = {
    default: 'dark:bg-white/10 bg-gray-100 dark:text-white text-gsw-navy border dark:border-white/20 border-gray-300 dark:hover:bg-white/20 hover:bg-gray-200 disabled:opacity-40',
    green:   'bg-green-600 text-white hover:bg-green-500 disabled:opacity-40',
    orange:  'bg-gsw-gold text-gsw-navy hover:bg-gsw-gold-light disabled:opacity-40',
    red:     'bg-red-700 text-white hover:bg-red-600 disabled:opacity-40',
    ghost:   'border border-dashed dark:border-white/20 border-gray-300 dark:text-white/30 text-gray-300 disabled:opacity-60',
  };
  return (
    <button
      onClick={onClick}
      disabled={disabled || loading}
      title={title}
      className={`${base} ${styles[variant]}`}
    >
      {loading ? '...' : children}
    </button>
  );
}

type DropdownItem =
  | { kind: 'button'; label: string; onClick: () => void; destructive?: boolean; disabled?: boolean }
  | { kind: 'toggle'; label: string; checked: boolean; onClick: () => void }
  | { kind: 'divider' };

// Estimate covers the longest possible menu (all items visible for a LIVE strategy).
const DROPDOWN_HEIGHT_ESTIMATE = 280;

function DropdownMenu({ items }: { items: DropdownItem[] }) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ top?: number; bottom?: number; right: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const openMenu = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const right = window.innerWidth - rect.right;
    const spaceBelow = window.innerHeight - rect.bottom;

    if (spaceBelow < DROPDOWN_HEIGHT_ESTIMATE && rect.top > spaceBelow) {
      // Not enough room below — flip upward. CSS `bottom` pins the menu's
      // bottom edge 6 px above the trigger's top edge.
      setCoords({ bottom: window.innerHeight - rect.top + 6, right });
    } else {
      setCoords({ top: rect.bottom + 6, right });
    }
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (
        !menuRef.current?.contains(e.target as Node) &&
        !triggerRef.current?.contains(e.target as Node)
      ) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  return (
    <>
      <button
        ref={triggerRef}
        onClick={openMenu}
        className="px-2 py-1 text-base dark:text-white/50 text-gray-500 dark:hover:text-white hover:text-gsw-navy dark:hover:bg-white/10 hover:bg-gray-100 rounded transition leading-none select-none"
        title="More actions"
      >
        ⋮
      </button>
      {open && coords && createPortal(
        <div
          ref={menuRef}
          style={{ position: 'fixed', top: coords.top, bottom: coords.bottom, right: coords.right, zIndex: 9999 }}
          className="w-52 dark:bg-gsw-navy bg-white border dark:border-white/20 border-gray-200 rounded-lg shadow-2xl overflow-hidden py-1"
        >
          {items.map((item, i) => {
            if (item.kind === 'divider') {
              return <div key={i} className="border-t dark:border-white/10 border-gray-200 my-1" />;
            }
            if (item.kind === 'toggle') {
              return (
                <button
                  key={i}
                  onClick={() => { item.onClick(); setOpen(false); }}
                  className="w-full flex items-center justify-between px-4 py-2.5 text-xs dark:text-white/90 text-gray-700 dark:hover:bg-white/10 hover:bg-gray-100 transition text-left"
                >
                  <span>{item.label}</span>
                  <span className={`w-7 h-4 rounded-full relative transition-colors shrink-0 ${item.checked ? 'bg-gsw-gold' : 'bg-white/25'}`}>
                    <span className={`absolute top-0.5 left-0.5 w-3 h-3 rounded-full bg-white shadow transition-transform ${item.checked ? 'translate-x-3' : 'translate-x-0'}`} />
                  </span>
                </button>
              );
            }
            return (
              <button
                key={i}
                onClick={() => { setOpen(false); item.onClick(); }}
                disabled={item.disabled}
                className={`w-full text-left px-4 py-2.5 text-xs transition disabled:opacity-40 disabled:cursor-not-allowed ${
                  item.destructive ? 'text-red-400 hover:bg-red-500/10' : 'dark:text-white/90 text-gray-700 dark:hover:bg-white/10 hover:bg-gray-100'
                }`}
              >
                {item.label}
              </button>
            );
          })}
        </div>,
        document.body
      )}
    </>
  );
}

function ModelIdCell({
  strategyId,
  strategyName,
  initial,
  onUpdate,
}: {
  strategyId: number;
  strategyName: string;
  initial: string | null;
  onUpdate: (updated: AdminStrategyRow) => void;
}) {
  const [value, setValue] = useState(initial ?? '');
  const [pending, setPending] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const handleSetClick = () => {
    const trimmed = value.trim();
    if (!trimmed || trimmed === (initial ?? '')) return;
    setPending(trimmed);
  };

  const handleConfirm = async () => {
    if (!pending) return;
    setSaving(true);
    setPending(null);
    try {
      const { data: updated } = await adminApi.setModelId(strategyId, pending);
      try {
        const { data: regenerated } = await adminApi.regenerateCsv(strategyId);
        onUpdate(regenerated);
      } catch {
        onUpdate(updated);
      }
    } catch {
      // leave input as-is so admin can retry
    } finally {
      setSaving(false);
    }
  };

  const isDirty = value.trim() !== (initial ?? '') && value.trim() !== '';

  return (
    <>
      <div className="flex items-center gap-1.5">
        <input
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSetClick()}
          placeholder="—"
          className="w-24 dark:bg-white/10 bg-gray-100 border dark:border-white/20 border-gray-300 rounded px-2 py-0.5 text-xs dark:text-white text-gsw-navy dark:placeholder-white/30 placeholder-gray-400 focus:outline-none focus:border-gsw-gold focus:ring-1 focus:ring-gsw-gold transition"
        />
        {isDirty && (
          <button
            onClick={handleSetClick}
            disabled={saving}
            className="px-2 py-0.5 text-xs rounded bg-gsw-gold text-gsw-navy font-medium hover:bg-gsw-gold-light disabled:opacity-50 transition"
          >
            {saving ? '…' : 'Set'}
          </button>
        )}
      </div>

      {pending !== null && (
        <div
          className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
          onClick={(e) => e.target === e.currentTarget && setPending(null)}
        >
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8">
            <h3 className="text-base font-semibold text-gray-900 mb-3">Confirm Model ID</h3>
            <p className="text-sm text-gray-600 leading-relaxed mb-6">
              {initial
                ? <>This will replace the existing Model ID <span className="font-mono font-semibold text-gray-800">{initial}</span> with <span className="font-mono font-semibold text-gray-800">{pending}</span>. Are you sure?</>
                : <>Are you sure you want to set the Model ID for <span className="font-semibold text-gray-800">{strategyName}</span> to <span className="font-mono font-semibold text-gray-800">{pending}</span>? This will be used in all future CSV exports and cannot be changed without confirmation.</>
              }
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setPending(null)}
                className="px-4 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                className="px-4 py-2 text-sm bg-gsw-gold text-gsw-navy rounded-lg hover:bg-gsw-gold-light transition font-semibold"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function RerunConfirmModal({
  strategyName,
  onConfirm,
  onCancel,
  loading,
}: {
  strategyName: string;
  onConfirm: () => void;
  onCancel: () => void;
  loading: boolean;
}) {
  return (
    <div
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
      onClick={(e) => e.target === e.currentTarget && !loading && onCancel()}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8">
        <h3 className="text-base font-semibold text-gray-900 mb-3">Re-run Strategy</h3>
        <p className="text-sm text-gray-600 leading-relaxed mb-6">
          This will re-execute <span className="font-semibold text-gray-800">{strategyName}</span> against
          the latest market data. If the output has changed meaningfully (new securities or weight shifts
          over 1%), a new CSV will be generated.
        </p>
        <div className="flex gap-3 justify-end">
          <button
            onClick={onCancel}
            disabled={loading}
            className="px-4 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className="px-4 py-2 text-sm bg-gsw-gold text-gsw-navy rounded-lg hover:bg-gsw-gold-light disabled:opacity-50 transition font-semibold"
          >
            {loading ? 'Running…' : 'Re-run'}
          </button>
        </div>
      </div>
    </div>
  );
}

function DeleteConfirmModal({
  strategyName,
  onConfirm,
  onCancel,
  loading,
}: {
  strategyName: string;
  onConfirm: () => void;
  onCancel: () => void;
  loading: boolean;
}) {
  return (
    <div
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
      onClick={(e) => e.target === e.currentTarget && !loading && onCancel()}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8">
        <h3 className="text-base font-semibold text-gray-900 mb-3">Delete Strategy</h3>
        <p className="text-sm text-gray-600 leading-relaxed mb-6">
          This will permanently delete{' '}
          <span className="font-semibold text-gray-800">{strategyName}</span> and all its version
          history. This cannot be undone.
        </p>
        <div className="flex gap-3 justify-end">
          <button
            onClick={onCancel}
            disabled={loading}
            className="px-4 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-500 disabled:opacity-50 transition font-semibold"
          >
            {loading ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}

function SetNameModal({
  row,
  onSave,
  onCancel,
}: {
  row: AdminStrategyRow;
  onSave: (updated: AdminStrategyRow) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(row.suggested_name ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setSaving(true);
    setError('');
    try {
      const { data } = await adminApi.setName(row.strategy_id, trimmed);
      onSave(data);
    } catch {
      setError('Failed to set strategy name.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
      onClick={(e) => e.target === e.currentTarget && !saving && onCancel()}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8">
        <h3 className="text-base font-semibold text-gray-900 mb-4">Set Strategy Name</h3>
        {row.suggested_name && (
          <p className="text-xs text-gray-500 mb-3">
            Quant suggested:{' '}
            <span className="font-medium text-gray-700 italic">{row.suggested_name}</span>
          </p>
        )}
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSave()}
          disabled={saving}
          maxLength={120}
          autoFocus
          className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gsw-gold focus:border-transparent transition mb-4"
          placeholder="Official strategy name"
        />
        {error && <p className="text-red-500 text-sm mb-3">{error}</p>}
        <div className="flex gap-3 justify-end">
          <button
            onClick={onCancel}
            disabled={saving}
            className="px-4 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !name.trim()}
            className="px-4 py-2 text-sm bg-gsw-gold text-gsw-navy rounded-lg hover:bg-gsw-gold-light disabled:opacity-50 transition font-semibold"
          >
            {saving ? 'Saving…' : 'Set Name'}
          </button>
        </div>
      </div>
    </div>
  );
}

function EditRequestModal({
  row,
  onApprove,
  onReject,
  onClose,
  approving,
  rejecting,
}: {
  row: AdminStrategyRow;
  onApprove: () => void;
  onReject: () => void;
  onClose: () => void;
  approving: boolean;
  rejecting: boolean;
}) {
  const busy = approving || rejecting;
  return (
    <div
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
      onClick={(e) => e.target === e.currentTarget && !busy && onClose()}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-8">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-gray-900">Edit Request</h3>
          <span className="text-xs text-gray-400">
            {row.edit_request_at
              ? new Date(row.edit_request_at).toLocaleString('en-GB', {
                  day: '2-digit', month: 'short', year: 'numeric',
                  hour: '2-digit', minute: '2-digit',
                })
              : ''}
          </span>
        </div>
        <p className="text-xs text-gray-500 mb-2">
          <span className="font-semibold text-gray-700">{row.quant_name}</span> has requested permission to edit:
        </p>
        <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-700 leading-relaxed whitespace-pre-wrap mb-6 max-h-48 overflow-y-auto">
          {row.edit_request_reason ?? '—'}
        </div>
        <div className="flex gap-3 justify-end">
          <button
            onClick={onClose}
            disabled={busy}
            className="px-4 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition"
          >
            Close
          </button>
          <button
            onClick={onReject}
            disabled={busy}
            className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-500 disabled:opacity-50 transition font-semibold"
          >
            {rejecting ? 'Rejecting…' : 'Reject'}
          </button>
          <button
            onClick={onApprove}
            disabled={busy}
            className="px-4 py-2 text-sm bg-gsw-gold text-gsw-navy rounded-lg hover:bg-gsw-gold-light disabled:opacity-50 transition font-semibold"
          >
            {approving ? 'Approving…' : 'Approve Edit'}
          </button>
        </div>
      </div>
    </div>
  );
}

function HistoryModal({
  strategyId,
  strategyName,
  onClose,
}: {
  strategyId: number;
  strategyName: string;
  onClose: () => void;
}) {
  const [versions, setVersions] = useState<StrategyVersionRow[] | null>(null);
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState<number | null>(null);

  useEffect(() => {
    adminApi.getHistory(strategyId)
      .then(({ data }) => setVersions(data))
      .catch(() => setError('Failed to load version history.'));
  }, [strategyId]);

  const handleDownload = async (v: StrategyVersionRow) => {
    setDownloading(v.id);
    try {
      const response = await adminApi.downloadVersionCsv(strategyId, v.id);
      const blob = new Blob([response.data as BlobPart], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${strategyName.replace(/\s+/g, '_')}_v${v.version_number}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch {
      setError('Failed to download version CSV.');
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="dark:bg-gsw-navy bg-white border dark:border-white/10 border-gray-200 rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col overflow-hidden max-h-[80vh]">
        <div className="shrink-0 flex items-center justify-between px-6 py-4 border-b dark:border-white/10 border-gray-200">
          <div>
            <h3 className="text-sm font-semibold dark:text-gray-100 text-gsw-navy">{strategyName}</h3>
            <p className="text-xs dark:text-white/40 text-gray-400 mt-0.5">Version history</p>
          </div>
          <button
            onClick={onClose}
            className="dark:text-white/40 text-gray-400 hover:text-white text-lg leading-none transition"
          >
            ✕
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {error && <p className="px-6 py-4 text-sm text-red-400">{error}</p>}
          {!error && versions === null && <p className="px-6 py-4 text-sm dark:text-white/40 text-gray-400">Loading…</p>}
          {!error && versions !== null && versions.length === 0 && (
            <p className="px-6 py-8 text-sm dark:text-white/30 text-gray-300 text-center">No versions yet</p>
          )}
          {!error && versions !== null && versions.length > 0 && (
            <table className="w-full text-sm">
              <thead className="border-b dark:border-white/10 border-gray-200 sticky top-0 dark:bg-gsw-navy bg-white">
                <tr>
                  {['Version', 'Run Date', 'EOD Date', 'Securities', 'Change', 'CSV'].map((h) => (
                    <th key={h} className="text-left px-5 py-3 text-xs font-semibold text-gsw-gold uppercase tracking-wide">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y dark:divide-white/10 divide-gray-200">
                {[...versions].reverse().map((v) => (
                  <tr key={v.id} className="dark:hover:bg-white/5 hover:bg-gray-50 transition-colors">
                    <td className="px-5 py-3 dark:text-white text-gsw-navy font-medium">v{v.version_number}</td>
                    <td className="px-5 py-3 dark:text-white/60 text-gray-500 text-xs">{fmtDateTime(v.run_date ?? '')}</td>
                    <td className="px-5 py-3 dark:text-white/60 text-gray-500 text-xs">{fmtDate(v.eod_data_date)}</td>
                    <td className="px-5 py-3 dark:text-white/70 text-gray-600">{v.security_count}</td>
                    <td className="px-5 py-3">
                      {v.meaningful_change === true && (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-gsw-gold/20 text-gsw-gold border border-gsw-gold/30">
                          <span className="w-1.5 h-1.5 rounded-full bg-gsw-gold shrink-0" />
                          Changed
                        </span>
                      )}
                      {v.meaningful_change === false && (
                        <span className="text-xs dark:text-white/30 text-gray-400">No change</span>
                      )}
                      {v.meaningful_change === null && (
                        <span className="dark:text-white/20 text-gray-300 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      {v.has_csv ? (
                        <button
                          onClick={() => handleDownload(v)}
                          disabled={downloading === v.id}
                          className="px-2 py-0.5 text-xs rounded dark:bg-white/10 bg-gray-100 dark:text-white text-gsw-navy border dark:border-white/20 border-gray-300 dark:hover:bg-white/20 hover:bg-gray-200 disabled:opacity-50 transition"
                        >
                          {downloading === v.id ? '…' : '↓ CSV'}
                        </button>
                      ) : (
                        <span className="dark:text-white/30 text-gray-300 text-xs">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function CodeViewModal({
  strategyId,
  strategyName,
  allowEdit,
  onClose,
}: {
  strategyId: number;
  strategyName: string;
  allowEdit: boolean;
  onClose: () => void;
}) {
  const [code, setCode] = useState<string | null>(null);
  const [draft, setDraft] = useState<string>('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    adminApi.getCode(strategyId)
      .then(({ data }) => { setCode(data.code); setDraft(data.code); })
      .catch(() => setError('Failed to load strategy code.'));
  }, [strategyId]);

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      const { data } = await adminApi.updateCode(strategyId, draft);
      setCode(data.code);
      setDraft(data.code);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      setError('Failed to save code changes.');
    } finally {
      setSaving(false);
    }
  };

  const isDirty = draft !== code;

  return (
    <div
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
      onClick={(e) => e.target === e.currentTarget && !saving && onClose()}
    >
      <div
        className="bg-[#1e1e1e] rounded-2xl shadow-2xl w-full max-w-4xl flex flex-col overflow-hidden"
        style={{ height: '80vh' }}
      >
        <div className="shrink-0 flex items-center justify-between px-6 py-4 border-b border-[#3c3c3c]">
          <div>
            <h3 className="text-sm font-semibold text-gray-100">{strategyName}</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {allowEdit ? 'Editable — changes are saved directly to the strategy' : 'Read-only'}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {allowEdit && (
              <>
                {saved && <span className="text-xs text-green-400">Saved</span>}
                <button
                  onClick={handleSave}
                  disabled={saving || !isDirty || code === null}
                  className="px-3 py-1.5 text-xs font-medium rounded-lg bg-gsw-gold text-gsw-navy hover:bg-gsw-gold-light disabled:opacity-40 disabled:cursor-not-allowed transition"
                >
                  {saving ? 'Saving…' : 'Save Changes'}
                </button>
              </>
            )}
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-200 text-lg leading-none transition"
            >
              ✕
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-hidden">
          {error && <p className="px-6 py-4 text-sm text-red-400">{error}</p>}
          {!error && code === null && <p className="px-6 py-4 text-sm text-gray-500">Loading…</p>}
          {!error && code !== null && (
            <Editor
              height="100%"
              defaultLanguage="python"
              value={draft}
              onChange={(val) => allowEdit && setDraft(val ?? '')}
              theme="vs-dark"
              options={{
                readOnly: !allowEdit,
                fontSize: 13,
                fontFamily: "'Fira Code', 'JetBrains Mono', Menlo, monospace",
                minimap: { enabled: false },
                scrollBeyondLastLine: false,
                wordWrap: 'on',
                lineNumbers: 'on',
                renderLineHighlight: 'line',
                automaticLayout: true,
                padding: { top: 16, bottom: 16 },
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function IndexChartModal({
  strategyId,
  strategyName,
  onClose,
}: {
  strategyId: number;
  strategyName: string;
  onClose: () => void;
}) {
  const [values, setValues] = useState<IndexValuesResponse | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    indexApi.getValues(strategyId)
      .then(({ data }) => setValues(data))
      .catch(() => setError('Failed to load index data.'));
  }, [strategyId]);

  const downloadCsv = () => {
    if (!values) return;
    const today = new Date().toISOString().split('T')[0];
    const filename = `${strategyName.replace(/\s+/g, '_')}_index_${today}.csv`;
    const header = ['Date', 'Index Value', 'Index Daily Return %', 'Portfolio Value', 'Portfolio Daily Return %', 'Portfolio Txn Cost', 'Portfolio Mgmt Fee', 'Rebalance'];
    const dateSet = new Set([...values.index.map((d) => d.date), ...values.portfolio.map((d) => d.date)]);
    const allDates = Array.from(dateSet).sort().reverse();
    const idxByDate = new Map(values.index.map((d) => [d.date, d]));
    const pfByDate = new Map(values.portfolio.map((d) => [d.date, d]));
    const rows = allDates.map((dt) => {
      const idx = idxByDate.get(dt);
      const pf = pfByDate.get(dt);
      return [
        dt,
        idx ? idx.index_value.toFixed(4) : '',
        idx?.daily_return_pct != null ? idx.daily_return_pct.toFixed(3) : '',
        pf ? pf.index_value.toFixed(4) : '',
        pf?.daily_return_pct != null ? pf.daily_return_pct.toFixed(3) : '',
        pf ? pf.txn_cost_drag.toFixed(4) : '',
        pf ? pf.fee_drag.toFixed(4) : '',
        (idx?.rebalance_signal || pf?.rebalance_signal) ? 'Yes' : 'No',
      ];
    });
    const csv = [header, ...rows].map((r) => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="dark:bg-gsw-navy bg-white border dark:border-white/10 border-gray-200 rounded-2xl shadow-2xl w-full max-w-3xl flex flex-col max-h-[90vh] overflow-hidden">
        <div className="shrink-0 flex items-center justify-between px-6 py-4 border-b dark:border-white/10 border-gray-200">
          <div>
            <h3 className="text-sm font-semibold dark:text-gray-100 text-gsw-navy">{strategyName}</h3>
            <p className="text-xs dark:text-white/40 text-gray-400 mt-0.5">Index performance since inception</p>
          </div>
          <div className="flex items-center gap-3">
            {values && (values.index.length > 0 || values.portfolio.length > 0) && (
              <button
                onClick={downloadCsv}
                className="px-2.5 py-1 text-xs rounded font-medium dark:bg-white/10 bg-gray-100 dark:text-white text-gsw-navy border dark:border-white/20 border-gray-300 dark:hover:bg-white/20 hover:bg-gray-200 transition"
                title="Download full series as CSV"
              >
                ↓ CSV
              </button>
            )}
            <button
              onClick={onClose}
              className="dark:text-white/40 text-gray-400 hover:text-white text-lg leading-none transition"
            >
              ✕
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {error && <p className="text-sm text-red-400">{error}</p>}
          {!error && values === null && (
            <p className="text-sm dark:text-white/40 text-gray-400">Loading…</p>
          )}
          {!error && values !== null && (
            <IndexChart data={values} height={300} />
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

type Tab = 'strategies' | 'data' | 'users' | 'audit' | 'settings';
type RowAction = 'downloading' | 'marking-live' | 'marking-inactive' | 'toggling-edit' | 'regenerating' | 'rerunning' | 'approving-edit' | 'rejecting-edit' | 'requesting-code-view' | null;

export default function AdminDashboard({ onSwitchToQuant }: { onSwitchToQuant?: () => void }) {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState<Tab>('strategies');
  const [strategies, setStrategies] = useState<AdminStrategyRow[]>([]);
  const [auditLog, setAuditLog] = useState<AuditLogEntry[]>([]);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [userFormEmail, setUserFormEmail] = useState('');
  const [userFormName, setUserFormName] = useState('');
  const [userFormRole, setUserFormRole] = useState<'quant' | 'gsw_admin'>('quant');
  const [userFormLoading, setUserFormLoading] = useState(false);
  const [userFormError, setUserFormError] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rowAction, setRowAction] = useState<Record<number, RowAction>>({});
  const [codeModal, setCodeModal] = useState<{ strategyId: number; strategyName: string; allowEdit: boolean } | null>(null);
  const [historyModal, setHistoryModal] = useState<{ strategyId: number; strategyName: string } | null>(null);
  const [rerunFor, setRerunFor] = useState<AdminStrategyRow | null>(null);
  const [rerunLoading, setRerunLoading] = useState(false);
  const [deleteFor, setDeleteFor] = useState<AdminStrategyRow | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [setNameFor, setSetNameFor] = useState<AdminStrategyRow | null>(null);
  const [editRequestModal, setEditRequestModal] = useState<AdminStrategyRow | null>(null);
  const [syncingAll, setSyncingAll] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [eodStatus, setEodStatus] = useState<EodStatusResponse | null>(null);
  const [indexOverview, setIndexOverview] = useState<Map<number, IndexOverviewRow>>(new Map());
  const [systemEodDate, setSystemEodDate] = useState<string | null>(null);
  const [indexChartFor, setIndexChartFor] = useState<{ strategyId: number; strategyName: string } | null>(null);

  // Settings tab
  const [txnCostHistory, setTxnCostHistory] = useState<PlatformSettingHistory | null>(null);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsError, setSettingsError] = useState('');
  const [newRate, setNewRate] = useState('0.20');
  const [newEffectiveFrom, setNewEffectiveFrom] = useState(() => {
    const t = new Date(); t.setDate(t.getDate() + 1);
    return t.toISOString().split('T')[0];
  });
  const [newNote, setNewNote] = useState('');
  const [settingsConfirm, setSettingsConfirm] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      adminApi.getAllStrategies(),
      adminApi.getAuditLog(),
      adminApi.getUsers(),
      eodApi.getStatus(),
      adminApi.getIndexOverview().catch(() => ({ data: null })),
    ])
      .then(([{ data: strats }, { data: log }, { data: usr }, { data: status }, { data: overview }]) => {
        setStrategies(strats);
        setAuditLog(log);
        setUsers(usr);
        setEodStatus(status);
        if (overview) {
          setSystemEodDate(overview.system_latest_eod_date);
          const map = new Map<number, IndexOverviewRow>();
          overview.strategies.forEach((r) => map.set(r.strategy_id, r));
          setIndexOverview(map);
        }
      })
      .catch((err: any) => {
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail;
        const code = err?.code ?? '';
        const msg = err?.message ?? '';
        if (status === 401 || status === 403) {
          setError(`Session expired or access denied (${status}). Please log out and sign in again.`);
        } else if (status >= 500) {
          setError(`Server error (${status})${detail ? ': ' + detail : ''}. Check the backend terminal.`);
        } else if (detail) {
          setError(`Failed to load admin data: ${detail}`);
        } else {
          setError(`Failed to load admin data — ${code || msg || 'unknown error'} (HTTP ${status ?? 'none'}). Check DevTools → Network tab.`);
        }
      })
      .finally(() => setLoading(false));
  }, []);

  const loadTxnCostHistory = () => {
    if (txnCostHistory || settingsLoading) return;
    setSettingsLoading(true);
    setSettingsError('');
    adminApi.getSettingHistory('transaction_cost_pct')
      .then(({ data }) => setTxnCostHistory(data))
      .catch(() => setSettingsError('Failed to load transaction cost history.'))
      .finally(() => setSettingsLoading(false));
  };

  const handleScheduleRate = async () => {
    const rate = parseFloat(newRate);
    if (isNaN(rate) || rate < 0) return;
    setSettingsSaving(true);
    setSettingsError('');
    try {
      await adminApi.createSetting('transaction_cost_pct', {
        value: rate,
        effective_from: newEffectiveFrom,
        note: newNote.trim() || undefined,
      });
      setSettingsConfirm(false);
      setTxnCostHistory(null); // force reload
      setNewNote('');
      loadTxnCostHistory();
      showToast('New transaction cost rate scheduled.');
    } catch (err: any) {
      const detail = err?.response?.data?.detail ?? 'Failed to schedule rate change.';
      setSettingsError(detail);
      setSettingsConfirm(false);
    } finally {
      setSettingsSaving(false);
    }
  };

  const setAction = (id: number, action: RowAction) =>
    setRowAction((prev) => ({ ...prev, [id]: action }));

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 5000);
  };

  const handleMarkLive = async (id: number) => {
    setAction(id, 'marking-live');
    try {
      const { data } = await adminApi.markLive(id);
      setStrategies((prev) => prev.map((s) => (s.strategy_id === id ? data : s)));
    } catch {
      setError('Failed to mark strategy as live.');
    } finally {
      setAction(id, null);
    }
  };

  const handleMarkInactive = async (id: number) => {
    setAction(id, 'marking-inactive');
    try {
      const { data } = await adminApi.markInactive(id);
      setStrategies((prev) => prev.map((s) => (s.strategy_id === id ? data : s)));
    } catch {
      setError('Failed to mark strategy as inactive.');
    } finally {
      setAction(id, null);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteFor) return;
    const id = deleteFor.strategy_id;
    setDeleteLoading(true);
    try {
      await adminApi.deleteStrategy(id);
      setDeleteFor(null);
      setStrategies((prev) => prev.filter((s) => s.strategy_id !== id));
      showToast('Strategy deleted.');
    } catch {
      setError('Failed to delete strategy.');
      setDeleteFor(null);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleToggleEdit = async (id: number) => {
    setAction(id, 'toggling-edit');
    try {
      const { data } = await adminApi.toggleEdit(id);
      setStrategies((prev) => prev.map((s) => (s.strategy_id === id ? data : s)));
    } catch {
      setError('Failed to toggle edit access.');
    } finally {
      setAction(id, null);
    }
  };

  const handleDownloadCsv = async (row: AdminStrategyRow) => {
    setAction(row.strategy_id, 'downloading');
    try {
      const response = await adminApi.downloadCsv(row.strategy_id);
      const blob = new Blob([response.data as BlobPart], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${row.strategy_name.replace(/\s+/g, '_')}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch {
      setError('Failed to download CSV.');
    } finally {
      setAction(row.strategy_id, null);
    }
  };

  const handleRegenerateCsv = async (id: number) => {
    setAction(id, 'regenerating');
    try {
      const { data } = await adminApi.regenerateCsv(id);
      setStrategies((prev) => prev.map((s) => (s.strategy_id === id ? data : s)));
    } catch {
      setError('Failed to regenerate CSV.');
    } finally {
      setAction(id, null);
    }
  };

  const handleRerunConfirm = async () => {
    if (!rerunFor) return;
    const id = rerunFor.strategy_id;
    setRerunLoading(true);
    try {
      const { data } = await adminApi.rerun(id);
      setRerunFor(null);
      setStrategies((prev) => prev.map((s) => (s.strategy_id === id ? data.strategy : s)));
      if (data.success) {
        const msg = data.meaningful_change
          ? `Meaningful change detected — new version generated`
          : `No meaningful change — output unchanged`;
        showToast(msg);
      } else {
        setError(`Re-run failed: ${data.errors.join('; ')}`);
      }
    } catch {
      setError('Re-run request failed.');
      setRerunFor(null);
    } finally {
      setRerunLoading(false);
    }
  };

  const handleApproveEdit = async (id: number) => {
    setAction(id, 'approving-edit');
    try {
      const { data } = await adminApi.approveEdit(id);
      setStrategies((prev) => prev.map((s) => (s.strategy_id === id ? data : s)));
      setEditRequestModal(null);
      showToast('Edit approved — strategy moved to Draft.');
    } catch {
      setError('Failed to approve edit request.');
    } finally {
      setAction(id, null);
    }
  };

  const handleRejectEdit = async (id: number) => {
    setAction(id, 'rejecting-edit');
    try {
      const { data } = await adminApi.rejectEdit(id);
      setStrategies((prev) => prev.map((s) => (s.strategy_id === id ? data : s)));
      setEditRequestModal(null);
      showToast('Edit request rejected — strategy remains Live.');
    } catch {
      setError('Failed to reject edit request.');
    } finally {
      setAction(id, null);
    }
  };

  const handleRequestCodeView = async (id: number) => {
    setAction(id, 'requesting-code-view');
    try {
      const { data } = await adminApi.requestCodeView(id);
      setStrategies((prev) => prev.map((s) => (s.strategy_id === id ? data : s)));
      showToast('Code view requested — the quant will be notified.');
    } catch {
      setError('Failed to send code view request.');
    } finally {
      setAction(id, null);
    }
  };

  const handleSyncAll = async () => {
    setSyncingAll(true);
    try {
      await adminApi.syncAll();
      const { data: freshStatus } = await eodApi.getStatus();
      setEodStatus(freshStatus);

      const fmtSyncDate = (iso: string | null) =>
        iso ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

      const dayGap = (freshStatus.latest_folder_date && freshStatus.latest_db_date)
        ? Math.round((new Date(freshStatus.latest_folder_date).getTime() - new Date(freshStatus.latest_db_date).getTime()) / 86400000)
        : 0;

      if (dayGap > 1) {
        showToast(`⚠ Sync complete but DB date (${fmtSyncDate(freshStatus.latest_db_date)}) is behind folder date (${fmtSyncDate(freshStatus.latest_folder_date)}) — check data folder`);
      } else {
        showToast(`Sync complete — DB up to date as of ${fmtSyncDate(freshStatus.latest_db_date ?? freshStatus.latest_folder_date)}`);
      }
    } catch (err: any) {
      const detail = err?.response?.data?.detail ?? 'Sync All failed.';
      showToast(`Error: ${detail}`);
    } finally {
      setSyncingAll(false);
    }
  };

  return (
    <div className="min-h-screen dark:bg-gsw-navy bg-gray-50 transition-colors duration-200">
      {/* Navbar */}
      <nav className="relative dark:bg-gsw-navy bg-white px-6 flex justify-between items-center border-b dark:border-white/10 border-gray-200 transition-colors duration-200" style={{ paddingTop: '15px', paddingBottom: '12px' }}>
        <div className="absolute top-0 left-0 right-0 h-[3px]" style={{ background: 'linear-gradient(90deg, #C9A84C, #E8C97A, #C9A84C)' }} />
        <div className="flex items-center gap-3">
          <img src="/gsw_logo.png" alt="GSW" className="h-9 object-contain" />
          <span className="text-base font-semibold text-gsw-gold font-playfair">
            GSW Quant Index Platform
          </span>
        </div>
        <div className="flex items-center gap-4">
          {onSwitchToQuant && (
            <button
              onClick={onSwitchToQuant}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-gsw-gold/60 bg-gsw-gold/10 text-gsw-gold hover:bg-gsw-gold/20 transition"
            >
              Switch to Quant View
            </button>
          )}
          <button
            onClick={handleSyncAll}
            disabled={syncingAll}
            title="Run all Vidal loaders: XM (security master) → WE (equity prices) → WY (InvIT prices) → XC (corporate actions)"
            className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-lg border border-gsw-gold/60 bg-gsw-gold/10 text-gsw-gold hover:bg-gsw-gold/20 disabled:opacity-50 disabled:cursor-not-allowed transition"
          >
            {syncingAll ? (
              <>
                <svg className="animate-spin h-3.5 w-3.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                </svg>
                Syncing…
              </>
            ) : (
              <>&#x21BB; Sync All Data</>
            )}
          </button>
          <span className="text-sm dark:text-white/70 text-gray-600">
            {user?.full_name}
            <span className="ml-2 bg-gsw-gold text-gsw-navy text-xs font-semibold px-2 py-0.5 rounded-full">
              GSW Admin
            </span>
          </span>
          <ThemeToggle />
          <button onClick={logout} className="text-sm dark:text-gsw-gold-light text-gsw-gold dark:hover:text-white hover:text-gsw-navy transition">
            Logout
          </button>
        </div>
      </nav>

      <main className="max-w-screen-xl mx-auto px-6 py-8">
        {/* Tabs */}
        <div className="flex gap-1 mb-6 border-b dark:border-white/10 border-gray-200">
          {(['strategies', 'data', 'users', 'audit', 'settings'] as Tab[]).map((t) => (
            <button
              key={t}
              onClick={() => { setTab(t); if (t === 'settings') loadTxnCostHistory(); }}
              className={`px-5 py-2.5 text-sm font-medium rounded-t transition ${
                tab === t
                  ? 'dark:bg-gsw-navy bg-white dark:border-b-gsw-navy border-b-white border dark:border-gsw-gold/40 border-gsw-gold/40 text-gsw-gold -mb-px'
                  : 'dark:text-white/50 text-gray-500 dark:hover:text-white hover:text-gsw-navy'
              }`}
            >
              {t === 'strategies' ? 'Strategies'
                : t === 'data' ? 'Data'
                : t === 'users' ? 'Users'
                : t === 'audit' ? 'Audit Log'
                : 'Settings'}
            </button>
          ))}
        </div>

        {error && (
          <div className="bg-red-900/30 border border-red-500/30 text-red-300 text-sm rounded-lg px-4 py-3 mb-6">
            {error}
          </div>
        )}

        {loading ? (
          <p className="dark:text-white/40 text-gray-400 text-sm">Loading…</p>
        ) : tab === 'data' ? (
          // ── Data tab ────────────────────────────────────────────────────
          <div className="space-y-4">
            <h2 className="text-base font-semibold dark:text-white text-gsw-navy">Data Pipeline Status</h2>
            {eodStatus ? (
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                {/* Today's files */}
                <div className="rounded-xl border dark:border-white/10 border-gray-200 p-5">
                  <p className="text-xs dark:text-white/40 text-gray-400 mb-1">Today's Files</p>
                  <span className={`inline-flex items-center gap-1.5 text-sm font-semibold ${eodStatus.today_files_received ? 'text-green-400' : 'text-red-400'}`}>
                    <span className={`h-2 w-2 rounded-full ${eodStatus.today_files_received ? 'bg-green-400' : 'bg-red-400'}`} />
                    {eodStatus.today_files_received ? 'Received' : 'Not yet received'}
                  </span>
                </div>
                {/* Latest DB date */}
                <div className="rounded-xl border dark:border-white/10 border-gray-200 p-5">
                  <p className="text-xs dark:text-white/40 text-gray-400 mb-1">Latest DB Date</p>
                  <p className="text-sm font-semibold dark:text-white text-gsw-navy">{eodStatus.latest_db_date ?? '—'}</p>
                </div>
                {/* Latest folder date */}
                <div className="rounded-xl border dark:border-white/10 border-gray-200 p-5">
                  <p className="text-xs dark:text-white/40 text-gray-400 mb-1">Latest Folder Date</p>
                  <p className="text-sm font-semibold dark:text-white text-gsw-navy">{eodStatus.latest_folder_date ?? '—'}</p>
                </div>
                {/* Price days */}
                <div className="rounded-xl border dark:border-white/10 border-gray-200 p-5">
                  <p className="text-xs dark:text-white/40 text-gray-400 mb-1">Price History Days</p>
                  <p className={`text-2xl font-bold ${eodStatus.price_days >= 30 ? 'text-green-400' : eodStatus.price_days >= 10 ? 'text-gsw-gold' : 'text-red-400'}`}>
                    {eodStatus.price_days}
                  </p>
                  <p className="text-xs dark:text-white/30 text-gray-300 mt-0.5">target: 30+ days</p>
                </div>
                {/* Symbol count */}
                <div className="rounded-xl border dark:border-white/10 border-gray-200 p-5">
                  <p className="text-xs dark:text-white/40 text-gray-400 mb-1">Symbols (latest day)</p>
                  <p className="text-2xl font-bold dark:text-white text-gsw-navy">{eodStatus.symbol_count.toLocaleString()}</p>
                </div>
                {/* Large/Mid Cap */}
                <div className="rounded-xl border dark:border-white/10 border-gray-200 p-5">
                  <p className="text-xs dark:text-white/40 text-gray-400 mb-1">Large + Mid Cap</p>
                  <p className={`text-2xl font-bold ${eodStatus.large_mid_cap_count >= 200 ? 'text-green-400' : 'text-red-400'}`}>
                    {eodStatus.large_mid_cap_count}
                  </p>
                  <p className="text-xs dark:text-white/30 text-gray-300 mt-0.5">from {eodStatus.fundamentals_count.toLocaleString()} with market cap</p>
                </div>
              </div>
            ) : (
              <p className="dark:text-white/40 text-gray-400 text-sm">No status data.</p>
            )}
          </div>
        ) : tab === 'users' ? (
          // ── Users tab ───────────────────────────────────────────────────
          <div className="space-y-6">
            {/* Create user form */}
            <div className="rounded-xl border dark:border-white/10 border-gray-200 p-6">
              <h3 className="text-sm font-semibold text-gsw-gold mb-4">Add User Account</h3>
              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  setUserFormLoading(true);
                  setUserFormError('');
                  try {
                    const { data } = await adminApi.createUser({
                      email: userFormEmail.trim(),
                      full_name: userFormName.trim(),
                      role: userFormRole,
                    });
                    setUsers((prev) => [data, ...prev]);
                    setUserFormEmail('');
                    setUserFormName('');
                    setUserFormRole('quant');
                  } catch (err: any) {
                    setUserFormError(err.response?.data?.detail || 'Failed to create user.');
                  } finally {
                    setUserFormLoading(false);
                  }
                }}
                className="flex flex-wrap gap-3 items-end"
              >
                <div className="flex-1 min-w-[180px]">
                  <label className="block text-xs dark:text-white/50 text-gray-500 mb-1">Full Name</label>
                  <input
                    type="text"
                    value={userFormName}
                    onChange={(e) => setUserFormName(e.target.value)}
                    required
                    placeholder="Vedang Shastri"
                    className="w-full dark:bg-white/5 bg-white border dark:border-white/20 border-gray-300 rounded-lg px-3 py-2 text-sm dark:text-white text-gsw-navy dark:placeholder-white/30 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-gsw-gold"
                  />
                </div>
                <div className="flex-1 min-w-[200px]">
                  <label className="block text-xs dark:text-white/50 text-gray-500 mb-1">Email</label>
                  <input
                    type="email"
                    value={userFormEmail}
                    onChange={(e) => setUserFormEmail(e.target.value)}
                    required
                    placeholder="vedang@thegoldstandard.in"
                    className="w-full dark:bg-white/5 bg-white border dark:border-white/20 border-gray-300 rounded-lg px-3 py-2 text-sm dark:text-white text-gsw-navy dark:placeholder-white/30 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-gsw-gold"
                  />
                </div>
                <div>
                  <label className="block text-xs dark:text-white/50 text-gray-500 mb-1">Role</label>
                  <select
                    value={userFormRole}
                    onChange={(e) => setUserFormRole(e.target.value as 'quant' | 'gsw_admin')}
                    className="dark:bg-white/5 bg-white border dark:border-white/20 border-gray-300 rounded-lg px-3 py-2 text-sm dark:text-white text-gsw-navy focus:outline-none focus:ring-1 focus:ring-gsw-gold"
                  >
                    <option value="quant" className="bg-gsw-navy">Quant</option>
                    <option value="gsw_admin" className="bg-gsw-navy">GSW Admin</option>
                  </select>
                </div>
                <button
                  type="submit"
                  disabled={userFormLoading}
                  className="bg-gsw-gold text-gsw-navy text-sm font-semibold px-4 py-2 rounded-lg hover:bg-gsw-gold-light disabled:opacity-50 transition"
                >
                  {userFormLoading ? 'Adding…' : '+ Add User'}
                </button>
              </form>
              {userFormError && (
                <p className="text-red-400 text-xs mt-2">{userFormError}</p>
              )}
              <p className="dark:text-white/30 text-gray-300 text-xs mt-3">
                The user will sign in via Microsoft SSO using the email address above. No password is required.
              </p>
            </div>

            {/* Users table */}
            <div className="rounded-xl border dark:border-white/10 border-gray-200 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b dark:border-white/10 border-gray-200">
                  <tr>
                    {['Name', 'Email', 'Role', 'Created'].map((h) => (
                      <th key={h} className="text-left px-5 py-3 font-semibold text-gsw-gold text-xs uppercase tracking-wide">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y dark:divide-white/10 divide-gray-200">
                  {users.length === 0 && (
                    <tr>
                      <td colSpan={4} className="px-5 py-12 text-center dark:text-white/30 text-gray-300">No users yet</td>
                    </tr>
                  )}
                  {users.map((u) => (
                    <tr key={u.id} className="dark:hover:bg-white/5 hover:bg-gray-50 transition-colors">
                      <td className="px-5 py-3 dark:text-white text-gsw-navy font-medium">{u.full_name}</td>
                      <td className="px-5 py-3 dark:text-white/60 text-gray-500 font-mono text-xs">{u.email}</td>
                      <td className="px-5 py-3">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                          u.role === 'gsw_admin'
                            ? 'bg-gsw-gold/20 text-gsw-gold border border-gsw-gold/30'
                            : 'dark:bg-white/10 bg-gray-100 dark:text-white/60 text-gray-500 border dark:border-white/20 border-gray-300'
                        }`}>
                          {u.role === 'gsw_admin' ? 'GSW Admin' : 'Quant'}
                        </span>
                      </td>
                      <td className="px-5 py-3 dark:text-white/40 text-gray-400 text-xs">{fmtDate(u.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : tab === 'strategies' ? (
          // ── Strategies table ────────────────────────────────────────────
          <div className="rounded-xl border dark:border-white/10 border-gray-200 overflow-x-auto">
            <table className="w-full text-sm min-w-[1000px]">
              <thead className="border-b dark:border-white/10 border-gray-200">
                <tr>
                  {['Quant', 'Strategy Name', 'Cadence', 'Last Rebalance', 'State', 'Last Checked', 'Valued As Of', 'Model ID', 'Actions'].map(
                    (h) => (
                      <th
                        key={h}
                        className="text-left px-5 py-3 font-semibold text-gsw-gold text-xs uppercase tracking-wide"
                      >
                        {h}
                      </th>
                    )
                  )}
                </tr>
              </thead>
              <tbody className="divide-y dark:divide-white/10 divide-gray-200">
                {strategies.length === 0 && (
                  <tr>
                    <td colSpan={9} className="px-5 py-12 text-center dark:text-white/30 text-gray-300">
                      No strategies found
                    </td>
                  </tr>
                )}
                {strategies.map((row) => {
                  const busy = rowAction[row.strategy_id];

                  const dropdownItems: DropdownItem[] = [];
                  if (row.state === 'EDIT_REQUESTED') {
                    dropdownItems.push({ kind: 'button', label: '✉ View Edit Request', onClick: () => setEditRequestModal(row) });
                    dropdownItems.push({ kind: 'divider' });
                  }
                  if (row.state === 'OUTPUT_READY') {
                    dropdownItems.push({ kind: 'button', label: '▶ Mark Live', onClick: () => handleMarkLive(row.strategy_id), disabled: busy === 'marking-live' });
                    dropdownItems.push({ kind: 'divider' });
                  }
                  if (row.state === 'LIVE') {
                    dropdownItems.push({ kind: 'button', label: '⊘ Mark as Inactive', onClick: () => handleMarkInactive(row.strategy_id), disabled: busy === 'marking-inactive' });
                    dropdownItems.push({ kind: 'divider' });
                  }
                  if (row.state === 'INACTIVE') {
                    dropdownItems.push({ kind: 'button', label: '▶ Mark Live', onClick: () => handleMarkLive(row.strategy_id), disabled: busy === 'marking-live' });
                    dropdownItems.push({ kind: 'divider' });
                  }
                  dropdownItems.push({ kind: 'button', label: '✎ Set Strategy Name', onClick: () => setSetNameFor(row) });
                  dropdownItems.push({ kind: 'button', label: '⧉ View History', onClick: () => setHistoryModal({ strategyId: row.strategy_id, strategyName: row.strategy_name }) });
                  if (row.allow_code_view) {
                    dropdownItems.push({ kind: 'button', label: '</> View / Edit Code', onClick: () => setCodeModal({ strategyId: row.strategy_id, strategyName: row.strategy_name, allowEdit: true }) });
                  } else if (row.code_view_requested) {
                    dropdownItems.push({ kind: 'button', label: '⌛ Code View Pending', onClick: () => {}, disabled: true });
                  } else {
                    dropdownItems.push({ kind: 'button', label: '⌁ Request Code View', onClick: () => handleRequestCodeView(row.strategy_id), disabled: busy === 'requesting-code-view' });
                  }
                  dropdownItems.push({ kind: 'toggle', label: 'Allow Edit', checked: row.edit_unlocked, onClick: () => handleToggleEdit(row.strategy_id) });
                  if (row.state === 'LIVE' && row.has_csv) {
                    dropdownItems.push({ kind: 'button', label: '↺ Regen CSV', onClick: () => handleRegenerateCsv(row.strategy_id), disabled: busy === 'regenerating' });
                  }
                  dropdownItems.push({ kind: 'divider' });
                  dropdownItems.push({ kind: 'button', label: '⊗ Delete', onClick: () => setDeleteFor(row), destructive: true });

                  const overview = indexOverview.get(row.strategy_id);

                  if (row.state === 'LIVE' && overview) {
                    dropdownItems.push({ kind: 'button', label: '📈 View Index', onClick: () => setIndexChartFor({ strategyId: row.strategy_id, strategyName: row.strategy_name }) });
                    dropdownItems.push({ kind: 'divider' });
                  }

                  return (
                    <tr key={row.strategy_id} className={`transition-colors ${row.state === 'EDIT_REQUESTED' ? 'bg-orange-900/20 hover:bg-orange-900/30' : 'dark:hover:bg-white/5 hover:bg-gray-50'}`}>
                      {/* Quant */}
                      <td className="px-5 py-3.5">
                        <p className="font-medium dark:text-white text-gsw-navy">{row.quant_name}</p>
                        <p className="dark:text-white/40 text-gray-400 text-xs">{row.quant_email}</p>
                      </td>
                      {/* Strategy Name */}
                      <td className="px-5 py-3.5">
                        {row.strategy_name === PENDING_NAME && row.suggested_name ? (
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="italic dark:text-white/70 text-gray-600">{row.suggested_name}</span>
                            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400 border border-amber-500/30 whitespace-nowrap">Suggested</span>
                            {row.last_run_status === 'failed' && (
                              <span
                                title={row.last_run_error ? row.last_run_error.slice(0, 200) : 'Last run failed'}
                                className="inline-block w-2 h-2 rounded-full bg-red-500 shrink-0 cursor-help"
                              />
                            )}
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <span className="dark:text-white text-gsw-navy font-medium">{row.strategy_name}</span>
                            {row.last_run_status === 'failed' && (
                              <span
                                title={row.last_run_error ? row.last_run_error.slice(0, 200) : 'Last run failed'}
                                className="inline-block w-2 h-2 rounded-full bg-red-500 shrink-0 cursor-help"
                              />
                            )}
                          </div>
                        )}
                      </td>
                      {/* Cadence */}
                      <td className="px-5 py-3.5 text-xs whitespace-nowrap">
                        <span className="dark:text-white/80 text-gray-700">
                          {row.rebalance_cadence
                            ? row.rebalance_cadence.charAt(0).toUpperCase() + row.rebalance_cadence.slice(1)
                            : '—'}
                        </span>
                        {row.change_mode && (
                          <span className="ml-1 dark:text-white/40 text-gray-400">
                            · {row.change_mode === 'all_changes' ? 'all' : `>${row.significance_threshold ?? 1}%`}
                          </span>
                        )}
                      </td>
                      {/* Last Rebalance */}
                      <td className="px-5 py-3.5 dark:text-white/60 text-gray-500 text-xs">
                        {fmtDate(row.eod_data_date)}
                      </td>
                      {/* State */}
                      <td className="px-5 py-3.5">
                        <StrategyStateBadge state={row.state} />
                      </td>
                      {/* Last Checked */}
                      <td className="px-5 py-3.5 dark:text-white/60 text-gray-500 text-xs">
                        {row.last_checked_at ? fmtDateTime(row.last_checked_at) : '—'}
                      </td>
                      {/* Valued As Of */}
                      <td className="px-5 py-3.5 text-xs whitespace-nowrap">
                        {overview ? (() => {
                          const vd = overview.portfolio_latest_date;
                          if (!vd) return <span className="dark:text-white/30 text-gray-300">Initialised</span>;
                          const isStale = row.state === 'LIVE' && systemEodDate !== null && vd < systemEodDate;
                          return (
                            <span className={`inline-flex items-center gap-1.5 ${isStale ? 'text-red-400' : 'dark:text-green-400 text-green-600'}`}>
                              <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${isStale ? 'bg-red-500' : 'bg-green-400'}`} />
                              {fmtDate(vd)}
                            </span>
                          );
                        })() : (
                          <span className="dark:text-white/30 text-gray-300">—</span>
                        )}
                      </td>
                      {/* Model ID */}
                      <td className="px-5 py-3.5">
                        <ModelIdCell
                          strategyId={row.strategy_id}
                          strategyName={row.strategy_name}
                          initial={row.model_id}
                          onUpdate={(updated) =>
                            setStrategies((prev) =>
                              prev.map((s) => (s.strategy_id === updated.strategy_id ? updated : s))
                            )
                          }
                        />
                      </td>
                      {/* Actions */}
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-1.5">
                          {DOWNLOADABLE.includes(row.state) && row.has_csv && (
                            <ActionBtn
                              onClick={() => handleDownloadCsv(row)}
                              loading={busy === 'downloading'}
                            >
                              ↓ CSV
                            </ActionBtn>
                          )}
                          {row.state === 'LIVE' && (
                            <ActionBtn
                              onClick={() => setRerunFor(row)}
                              loading={busy === 'rerunning'}
                              variant="orange"
                              title="Re-execute with latest EOD data"
                            >
                              ↺ Re-run
                            </ActionBtn>
                          )}
                          <DropdownMenu items={dropdownItems} />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : tab === 'settings' ? (
          // ── Settings tab ────────────────────────────────────────────────
          <div className="space-y-6">
            <div>
              <h2 className="text-base font-semibold dark:text-white text-gsw-navy mb-1">
                Transaction Cost Rate
              </h2>
              <p className="text-xs dark:text-white/40 text-gray-400">
                Covers brokerage, STT, stamp duty, exchange charges and GST. Applied to equity trades only — mutual funds use direct plans with no transaction cost. Historical index values are not recomputed when a new rate takes effect.
              </p>
            </div>

            {settingsError && (
              <div className="bg-red-900/30 border border-red-500/30 text-red-300 text-sm rounded-lg px-4 py-3">
                {settingsError}
              </div>
            )}

            {/* History table */}
            <div className="rounded-xl border dark:border-white/10 border-gray-200 overflow-x-auto">
              {settingsLoading ? (
                <p className="px-5 py-8 text-sm dark:text-white/40 text-gray-400">Loading…</p>
              ) : txnCostHistory ? (
                <table className="w-full text-sm">
                  <thead className="border-b dark:border-white/10 border-gray-200">
                    <tr>
                      {['Effective From', 'Rate', 'Set By', 'Note', 'Status'].map((h) => (
                        <th key={h} className="text-left px-5 py-3 font-semibold text-gsw-gold text-xs uppercase tracking-wide">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y dark:divide-white/10 divide-gray-200">
                    {txnCostHistory.history.map((row) => {
                      const isCurrent = row.effective_from === txnCostHistory.current_effective_from;
                      const setByUser = users.find((u) => u.id === row.created_by_user_id);
                      return (
                        <tr
                          key={row.id}
                          className={`transition-colors ${isCurrent ? 'dark:bg-gsw-gold/5 bg-gsw-gold/5' : 'dark:hover:bg-white/5 hover:bg-gray-50'}`}
                        >
                          <td className="px-5 py-3 dark:text-white text-gsw-navy font-medium">
                            {fmtDate(row.effective_from)}
                          </td>
                          <td className="px-5 py-3 font-mono dark:text-white text-gsw-navy">
                            {row.value.toFixed(2)}%
                          </td>
                          <td className="px-5 py-3 dark:text-white/60 text-gray-500 text-xs">
                            {setByUser ? setByUser.full_name : '—'}
                          </td>
                          <td className="px-5 py-3 dark:text-white/60 text-gray-500 text-xs max-w-xs">
                            {row.note ?? '—'}
                          </td>
                          <td className="px-5 py-3">
                            {isCurrent && (
                              <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-gsw-gold/20 text-gsw-gold border border-gsw-gold/30">
                                <span className="w-1.5 h-1.5 rounded-full bg-gsw-gold shrink-0" />
                                Active
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {txnCostHistory.history.length === 0 && (
                      <tr>
                        <td colSpan={5} className="px-5 py-12 text-center dark:text-white/30 text-gray-300">
                          No settings found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              ) : null}
            </div>

            {/* Schedule new rate form */}
            <div className="rounded-xl border dark:border-white/10 border-gray-200 p-6">
              <h3 className="text-sm font-semibold text-gsw-gold mb-4">Schedule a Rate Change</h3>
              <div className="flex flex-wrap gap-4 items-end">
                <div>
                  <label className="block text-xs dark:text-white/50 text-gray-500 mb-1">
                    New Rate (%)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={newRate}
                    onChange={(e) => setNewRate(e.target.value)}
                    className="w-28 dark:bg-white/5 bg-white border dark:border-white/20 border-gray-300 rounded-lg px-3 py-2 text-sm dark:text-white text-gsw-navy focus:outline-none focus:ring-1 focus:ring-gsw-gold font-mono"
                  />
                </div>
                <div>
                  <label className="block text-xs dark:text-white/50 text-gray-500 mb-1">
                    Effective From
                  </label>
                  <input
                    type="date"
                    value={newEffectiveFrom}
                    onChange={(e) => setNewEffectiveFrom(e.target.value)}
                    className="dark:bg-white/5 bg-white border dark:border-white/20 border-gray-300 rounded-lg px-3 py-2 text-sm dark:text-white text-gsw-navy focus:outline-none focus:ring-1 focus:ring-gsw-gold"
                  />
                </div>
                <div className="flex-1 min-w-[220px]">
                  <label className="block text-xs dark:text-white/50 text-gray-500 mb-1">
                    Note (optional)
                  </label>
                  <input
                    type="text"
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    placeholder="e.g. STT increased in FY27 budget"
                    className="w-full dark:bg-white/5 bg-white border dark:border-white/20 border-gray-300 rounded-lg px-3 py-2 text-sm dark:text-white text-gsw-navy dark:placeholder-white/30 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-gsw-gold"
                  />
                </div>
                <button
                  onClick={() => setSettingsConfirm(true)}
                  disabled={!newRate || isNaN(parseFloat(newRate)) || parseFloat(newRate) < 0 || !newEffectiveFrom}
                  className="bg-gsw-gold text-gsw-navy text-sm font-semibold px-4 py-2 rounded-lg hover:bg-gsw-gold-light disabled:opacity-50 transition"
                >
                  Schedule Rate Change
                </button>
              </div>
            </div>

            {/* Confirm modal */}
            {settingsConfirm && (
              <div
                className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
                onClick={(e) => e.target === e.currentTarget && !settingsSaving && setSettingsConfirm(false)}
              >
                <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8">
                  <h3 className="text-base font-semibold text-gray-900 mb-3">Confirm Rate Change</h3>
                  <p className="text-sm text-gray-600 leading-relaxed mb-3">
                    This will set the platform transaction cost rate to{' '}
                    <span className="font-semibold text-gray-800 font-mono">{parseFloat(newRate).toFixed(2)}%</span>{' '}
                    effective from{' '}
                    <span className="font-semibold text-gray-800">{fmtDate(newEffectiveFrom)}</span>.
                  </p>
                  <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-6">
                    Historical index values are <strong>not recomputed</strong>. This rate will apply only to index engine valuations run on or after the effective date.
                  </p>
                  <div className="flex gap-3 justify-end">
                    <button
                      onClick={() => setSettingsConfirm(false)}
                      disabled={settingsSaving}
                      className="px-4 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleScheduleRate}
                      disabled={settingsSaving}
                      className="px-4 py-2 text-sm bg-gsw-gold text-gsw-navy rounded-lg hover:bg-gsw-gold-light disabled:opacity-50 transition font-semibold"
                    >
                      {settingsSaving ? 'Saving…' : 'Confirm'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          // ── Audit log table ─────────────────────────────────────────────
          <div className="rounded-xl border dark:border-white/10 border-gray-200 overflow-x-auto">
            <table className="w-full text-sm min-w-[700px]">
              <thead className="border-b dark:border-white/10 border-gray-200">
                <tr>
                  {['Timestamp', 'Strategy', 'Quant', 'Transition', 'Actor'].map((h) => (
                    <th
                      key={h}
                      className="text-left px-5 py-3 font-semibold text-gsw-gold text-xs uppercase tracking-wide"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y dark:divide-white/10 divide-gray-200">
                {auditLog.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-5 py-12 text-center dark:text-white/30 text-gray-300">
                      No audit events yet
                    </td>
                  </tr>
                )}
                {auditLog.map((entry) => (
                  <tr key={entry.id} className="dark:hover:bg-white/5 hover:bg-gray-50 transition-colors">
                    <td className="px-5 py-3 dark:text-white/60 text-gray-500 text-xs whitespace-nowrap">
                      {fmtDateTime(entry.created_at)}
                    </td>
                    <td className="px-5 py-3 dark:text-white text-gsw-navy font-medium">
                      {entry.strategy_name}
                    </td>
                    <td className="px-5 py-3 dark:text-white/70 text-gray-600">{entry.quant_name}</td>
                    <td className="px-5 py-3">
                      <span className="inline-flex items-center gap-1.5 text-xs">
                        {entry.old_state ? (
                          <StrategyStateBadge state={entry.old_state as StrategyState} />
                        ) : (
                          <span className="dark:text-white/30 text-gray-300">—</span>
                        )}
                        <span className="dark:text-white/30 text-gray-300">→</span>
                        <StrategyStateBadge state={entry.new_state as StrategyState} />
                      </span>
                    </td>
                    <td className="px-5 py-3 dark:text-white/60 text-gray-500 text-xs font-mono">
                      {entry.actor}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {/* Index chart modal */}
      {indexChartFor && (
        <IndexChartModal
          strategyId={indexChartFor.strategyId}
          strategyName={indexChartFor.strategyName}
          onClose={() => setIndexChartFor(null)}
        />
      )}

      {/* Set name modal */}
      {setNameFor && (
        <SetNameModal
          row={setNameFor}
          onSave={(updated) => {
            setStrategies((prev) => prev.map((s) => (s.strategy_id === updated.strategy_id ? updated : s)));
            setSetNameFor(null);
          }}
          onCancel={() => setSetNameFor(null)}
        />
      )}

      {/* Edit request modal */}
      {editRequestModal && (
        <EditRequestModal
          row={editRequestModal}
          onApprove={() => handleApproveEdit(editRequestModal.strategy_id)}
          onReject={() => handleRejectEdit(editRequestModal.strategy_id)}
          onClose={() => setEditRequestModal(null)}
          approving={rowAction[editRequestModal.strategy_id] === 'approving-edit'}
          rejecting={rowAction[editRequestModal.strategy_id] === 'rejecting-edit'}
        />
      )}

      {/* Delete confirmation modal */}
      {deleteFor && (
        <DeleteConfirmModal
          strategyName={deleteFor.strategy_name}
          onConfirm={handleDeleteConfirm}
          onCancel={() => !deleteLoading && setDeleteFor(null)}
          loading={deleteLoading}
        />
      )}

      {/* Re-run confirmation modal */}
      {rerunFor && (
        <RerunConfirmModal
          strategyName={rerunFor.strategy_name}
          onConfirm={handleRerunConfirm}
          onCancel={() => !rerunLoading && setRerunFor(null)}
          loading={rerunLoading}
        />
      )}

      {/* Version history modal */}
      {historyModal && (
        <HistoryModal
          strategyId={historyModal.strategyId}
          strategyName={historyModal.strategyName}
          onClose={() => setHistoryModal(null)}
        />
      )}

      {/* View / edit code modal */}
      {codeModal && (
        <CodeViewModal
          strategyId={codeModal.strategyId}
          strategyName={codeModal.strategyName}
          allowEdit={codeModal.allowEdit}
          onClose={() => setCodeModal(null)}
        />
      )}

      {/* Toast notification */}
      {toast && (
        <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-4 py-3 rounded-xl shadow-2xl text-sm text-white max-w-sm ${
          toast.startsWith('⚠')
            ? 'bg-amber-900/90 border border-amber-500/50'
            : 'bg-gsw-navy border border-gsw-gold/40'
        }`}>
          {toast.startsWith('⚠')
            ? <span className="text-amber-400 text-base shrink-0">⚠</span>
            : <span className="text-gsw-gold text-base shrink-0">✓</span>
          }
          <span>{toast.startsWith('⚠') ? toast.slice(2) : toast}</span>
          <button
            onClick={() => setToast(null)}
            className="ml-auto dark:text-white/40 text-gray-400 hover:text-white transition text-base leading-none"
          >
            ✕
          </button>
        </div>
      )}
    </div>
  );
}
